import { css } from "@emotion/react";
import { FC } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";

import {
  Adjust,
  Apps,
  Automation,
  CannedResponse,
  Notifications,
  Palette,
  Send,
  Tag,
} from "@livechat/design-system-icons";
import { DesignToken, Heading, RadiusToken, SpacingToken } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { config } from "config/config";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { isInIframe } from "helpers/app";
import { AgentFormSectionId } from "routes/agents/agent-form/constants";
import { AgentsRoutes } from "routes/agents/constants";
import { AutomationsRoutes } from "routes/automations/constants";
import { SettingsRoutes } from "routes/settings/constants";
import { getCurrentAgentId } from "store/features/session";
import { withVar } from "themes/helpers";

import { NextStepItem, NextStepItemProps } from "./NextStepItem";

type OnboardingNextStep =
  | "customFields"
  | "emailTemplates"
  | "domain"
  | "notifications"
  | "cannedResponses"
  | "tags"
  | "marketplace"
  | "teams"
  | "automations"
  | "contactForm";

export const OnboardingNextSteps: FC = () => {
  const history = useHistory();
  const appInFrame = isInIframe();
  const currentAgentId = useSelector(getCurrentAgentId);

  const handleStepClick = (url: string, itemName: OnboardingNextStep): void => {
    trackEngagement("Checklist: Redirect links clicked", EventPlace.Onboarding, {
      link: itemName,
    });
    if (appInFrame) {
      window.open(`${config.appUrl}${url}`);
    } else {
      history.push(url);
    }
  };

  const steps: Array<NextStepItemProps> = [
    {
      icon: Palette,
      title: "Customize email templates",
      description: "Define the final look of your emails",
      onClick: () => handleStepClick(SettingsRoutes.EmailTemplates, "emailTemplates"),
    },
    {
      icon: CannedResponse,
      title: "Create canned responses",
      description: "Have ready-made content at hand",
      onClick: () => handleStepClick(SettingsRoutes.CannedResponses, "cannedResponses"),
    },
    {
      icon: Tag,
      title: "Manage tags",
      description: "Add descriptive labels to tickets",
      onClick: () => handleStepClick(SettingsRoutes.Tags, "tags"),
    },
    {
      icon: Adjust,
      title: "Create custom fields",
      description: "Equip tickets with additional info",
      onClick: () => handleStepClick(SettingsRoutes.CustomFields, "customFields"),
    },
    {
      title: "Create contact form",
      description: "Build a tailored contact form for your website",
      onClick: () => handleStepClick(SettingsRoutes.ContactForms, "contactForm"),
      icon: Send,
    },
    {
      icon: Automation,
      title: "Set up automations",
      description: "Auto-solve common customer cases",
      onClick: () => handleStepClick(AutomationsRoutes.Rules, "automations"),
    },
    {
      icon: Notifications,
      title: "Adjust notifications",
      description: "Stay up to date with ticket news",
      onClick: () =>
        handleStepClick(
          `${AgentsRoutes.Agents}/${currentAgentId}/edit/#${AgentFormSectionId.BrowserNotifications}`,
          "notifications"
        ),
    },
    {
      icon: Apps,
      title: "Explore Marketplace",
      description: "Integrate HelpDesk with other apps",
      onClick: () => handleStepClick("/marketplace", "marketplace"),
    },
  ];

  return (
    <Flex gap={withVar(SpacingToken.Spacing5)} column>
      <Heading
        size="sm"
        css={css`
          width: fit-content;
        `}
      >
        Use other powerful features
      </Heading>
      <Flex
        column
        border={`1px solid ${withVar(DesignToken.BorderBasicSecondary)}`}
        borderRadius={withVar(RadiusToken.Radius3)}
      >
        {steps.map(step => (
          <NextStepItem key={step.title} {...step} />
        ))}
      </Flex>
    </Flex>
  );
};
